import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { ReedStateHistory } from 'lavva.exalushome/build/js/Services/StatesHistory/StatesHistory';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { ControlWrapper, DatePicker, TimeRanges } from '../../../../../../components';
import { IconPadlock } from '../../../../../../components/icons';
import { useInstallationContext } from '../../../../../../context/installation';
import { useChartData, useChartSupportPagination } from '../../../../hooks/charts';
import { ChartEmptyContainer } from '../components/chart-empty-container/chart-empty-container';
import ChartPagination, { defaultLimit } from '../components/chart-pagination';
import CustomizedXTick from '../components/customized-x-tick';
import DetailsList from '../components/details-list';
import { useNavigateCharts } from '../hooks/use-history-charts';
import { stateInterval } from '../types';

type ReedChartProps = {
  channel: IDeviceChannel;
  active?: boolean;
};

const ReedChart: React.FC<ReedChartProps> = ({ channel, active }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const { selectedInstallation } = useInstallationContext();
  const { data: supported } = useChartSupportPagination();
  const { offSet, setOffSet, activeRange, onClickActiveMeasurementRange, formatDate } = useNavigateCharts();
  const { data: chartData, isLoading } = useChartData<ReedStateHistory>(
    channel,
    DeviceResponseType.ReedState,
    stateInterval[activeRange],
    defaultLimit,
    offSet,
  );

  const data = useMemo(
    () =>
      chartData?.Data.map((x) => ({
        date: formatDate(x.Time),
        reed: x.Values?.State ? 1 : 0,
      })),
    [chartData?.Data],
  );

  return (
    <>
      <ControlWrapper className={classNames('control-wrapper--full-space no-center', { active: active })}>
        <div className="exalus-chart-container">
          <TimeRanges
            activeMeasurementRange={activeRange}
            setActiveMeasurementRange={onClickActiveMeasurementRange}
            disableMonth
            disableYear
          />
          <DatePicker
            showDateButtons={false}
            activeDay={new Date()}
            activeMeasurementRange={activeRange}
            setActiveDay={() => null}
          />
          {data && data.length > 0 ? (
            <>
              <div className="binary-chart">
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart
                    width={500}
                    height={150}
                    data={data}
                    margin={{
                      top: 15,
                      right: 20,
                      left: 30,
                      bottom: 0,
                    }}
                  >
                    <Bar dataKey="reed" fill={selectedInstallation?.hexColor} />
                    <XAxis
                      dataKey="date"
                      tick={<CustomizedXTick />}
                      height={44}
                      interval="preserveStartEnd"
                      allowDuplicatedCategory={false}
                    />
                    <YAxis max={1} min={0} hide />
                  </BarChart>
                </ResponsiveContainer>
                <div className="axis-icon">
                  <IconPadlock isOn={true} />
                  <span>{tc('opened')}</span>
                </div>
                <div className="axis-icon">
                  <IconPadlock isOn={false} />
                  <span>{tc('closed')}</span>
                </div>
                <ChartPagination
                  offSet={offSet}
                  setOffSet={setOffSet}
                  isNextAvailable={!!chartData?.IsNextPageAvailable}
                  visible={supported}
                />
              </div>
              <DetailsList data={chartData?.Data} />
            </>
          ) : (
            <ChartEmptyContainer isLoading={isLoading} />
          )}
        </div>
      </ControlWrapper>
    </>
  );
};

export default ReedChart;
